import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import GridLight from "highcharts/themes/grid-light";

// Apply the "grid light" theme globally
GridLight(Highcharts);

const LineChart = ({ data }) => {
  const [chartOptions, setChartOptions] = useState({});
  const [sortedDates, setSortedDates] = useState([]);

  useEffect(() => {
    if (!Array.isArray(data)) {
      console.error("Data should be an array");
      return;
    }

    // Transform the server data into the format needed by Highcharts
    const { transformedData, dates } = transformData(data);

    setSortedDates(dates);

    const options = {
      credits: {
        enabled: false,
      },
      title: {
        text: "Timeline of Activity",
      },
      colors: ["#45FFCA", "#D67BFF", "#FF7777", "#5B8FB9", "#FFB6D9"], // Custom colors
      chart: {
        type: "line",
        backgroundColor: "#ffffff", // Set background to white to prevent turning black in fullscreen
      },
      xAxis: {
        type: "datetime",
        labels: {
          formatter: function () {
            return Highcharts.dateFormat("%b %d", this.value); // Format as "Month Day"
          },
        },
      },

      yAxis: {
        title: {
          text: null, // Remove text from the y-axis
        },
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          return `
            <b>Total Sessions:</b> ${point.sessionCount}<br/>
            <b>Crawled Posts:</b> ${point.y}<br/>
            <b>Unlawful Posts:</b> ${point.unlawfulCount || 0}<br/>
          `;
        },
      },
      series: transformedData.map((series) => ({
        ...series,
        connectNulls: true, // This ensures that the line continues even if data points are missing
        lineWidth: 2.5, // Increase this value to make the line thicker
        marker: {
          radius: 6, // Increase this value to make the markers larger
        },
      })),
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 600,
            },
            chartOptions: {
              title: {
                style: {
                  fontSize: "16px",
                },
              },
              xAxis: {
                labels: {
                  style: {
                    fontSize: "20px",
                  },
                },
              },
              yAxis: {
                labels: {
                  style: {
                    fontSize: "10px",
                    fontWeight: "bold",
                  },
                },
              },
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
    };

    setChartOptions(options);
  }, [data]);

  const transformData = (data) => {
    const platformMap = {};
    const allDates = new Set();

    data.forEach((item) => {
      const {
        platform,
        postCount,
        sessionId,
        dateCreated,
        unlawfulCount,
        timeTaken,
      } = item;

      if (!dateCreated) {
        console.error(`Invalid date: ${dateCreated}`);
        return;
      }

      const date = new Date(dateCreated);

      if (isNaN(date.getTime())) {
        console.error(`Invalid date: ${dateCreated}`);
        return;
      }

      const dateStr = date.toISOString().split("T")[0]; // Extract the date in YYYY-MM-DD format

      if (!platformMap[platform]) {
        platformMap[platform] = {};
      }

      if (!platformMap[platform][dateStr]) {
        platformMap[platform][dateStr] = {
          postCount: 0,
          sessionIds: [],
          unlawfulCount: 0,
          timeTaken: "0:00:00.000000",
        };
      }

      platformMap[platform][dateStr].postCount += postCount;
      platformMap[platform][dateStr].sessionIds.push(sessionId);
      platformMap[platform][dateStr].unlawfulCount += unlawfulCount || 0;
      platformMap[platform][dateStr].timeTaken = timeTaken; // Overwrite with the latest timeTaken (can be adjusted as needed)

      allDates.add(new Date(dateStr).getTime());
    });

    const sortedDates = Array.from(allDates).sort((a, b) => a - b);
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];

   const fillMissingDates = (dateMap) => {
     const filledMap = {};
     for (let d = startDate; d <= endDate; d += 86400000) {
       // Increment by one day in milliseconds
       const dateStr = new Date(d).toISOString().split("T")[0];
       // If there's no data for this date, fill with default values
       filledMap[dateStr] = dateMap[dateStr] || {
         postCount: 0,
         sessionIds: [],
         unlawfulCount: 0,
         timeTaken: "0:00:00.000000",
       };
     }
     return filledMap;
   };

// console.log(
//   "Data for Twitter and YouTube after October 2:",
//   platformMap["TWITTER"],
//   platformMap["YOUTUBE"]
// );

    return {
      transformedData: Object.keys(platformMap).map((platform) => ({
        name: platform,
        data: Object.entries(fillMissingDates(platformMap[platform])).map(
          ([date, { postCount, sessionIds, unlawfulCount, timeTaken }]) => ({
            x: new Date(date).getTime(),
            y: postCount,
            sessionCount: sessionIds.length, // Count of session IDs
            dateCreated: date,
            unlawfulCount,
            timeTaken,
          })
        ),
      })),
      dates: sortedDates,
    };
  };

  // Add useEffect hook to handle window resize events
  useEffect(() => {
    const handleResize = () => {
      // Reflow all Highcharts instances to adjust to the new size
      Highcharts.charts.forEach((chart) => {
        if (chart) {
          chart.reflow();
        }
      });
    };

    // Add event listener for resize
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export default LineChart;
